/**
 * Pending-generation handoff between the Generate page and the /created
 * gallery. The Generate page records the request before starting Phase A and
 * navigates immediately. The gallery uses this state to show an in-progress
 * source shelf until placeholder posts exist and to continue first-post timing.
 *
 * sessionStorage (per-tab, survives reload) holds an ARRAY of requests so
 * starting a second generation never erases the first one's tiles.
 */

export type PendingSourceType = "url" | "text" | "youtube" | "pdf";

export type PendingSource = {
  /** Stable identity within a generation request, independent of URL/text. */
  sourceId?: string;
  type: PendingSourceType;
  /** Human identity for the tile: hostname, page title, or "Pasted text". */
  label: string;
  favicon?: string;
  /** Exact value written to Post.generationSource; joins the waiting source
   *  to its generated posts without relying on a display label. */
  generationSource?: string;
};

export type GenerationRetry = {
  sources: { id: string; type: "url" | "text"; value: string; documentId?: string; validationId?: string; title?: string }[];
  generationMode: "batch" | "synthesis";
  synthesisPrompt: string;
  postsPerSourceCap: string;
  preserveInputAsCaption: boolean;
  targetLanguage: string;
  selectedVisualStyleId: string;
  selectedBrandKitId: string | null;
  selectedConnectionIds: string[];
  templateIds: string[];
};

export type PendingRequest = {
  /** Response lost; not a confirmed failure and must not offer a fresh retry. */
  confirming?: boolean;
  error?: string;
  suggestion?: string;
  fallback?: { type: "paste_text" | "paste_transcript"; url: string };
  errorCode?: "insufficient_credits" | "no-source-images" | "pdf-unreadable";
  retryData?: GenerationRetry;
  clientRequestId: string;
  clickedAt: number;
  sources: PendingSource[];
  /** Per-source post cap when the user set one (1-10); absent = AI decides. */
  maxPostsPerSource?: number;
  generationQualityVariant?:
    | "pre-aug5"
    | "aug5-bundle"
    | "explicit-style-control"
    | "explicit-style-anchor";
  /** Filled in when the server responds — enables request completion timing. */
  postIds?: string[];
  /** Set once the click→first-post metric has fired, so it fires only once. */
  firstPostSeen?: boolean;
  /** Wall-clock time used to measure the first→last post straggler gap. */
  firstPostSeenAt?: number;
};

const KEY_PREFIX = "wavegen_gen_pending";
export const PENDING_MAX_AGE_MS = 5 * 60 * 1000;

/** Missing protected docs surface as permission errors until the server's
 * transaction creates them. Intermediate listener failures are expected; only
 * the terminal attempt is actionable and should be reported as an error. */
export function shouldReportPendingProgressFailure(
  retriesLeft: number,
): boolean {
  return retriesLeft <= 0;
}

function storageKey(workspaceId: string | null | undefined): string | null {
  return workspaceId ? `${KEY_PREFIX}:${workspaceId}` : null;
}

function readStoredRequests(
  workspaceId: string | null | undefined,
): PendingRequest[] {
  const key = storageKey(workspaceId);
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { requests?: PendingRequest[] };
    const requests = Array.isArray(parsed.requests) ? parsed.requests : [];
    return requests.filter(
      (r) =>
        typeof r?.clientRequestId === "string" &&
        typeof r?.clickedAt === "number" &&
        Array.isArray(r?.sources) &&
        Date.now() - r.clickedAt <= 24 * 60 * 60 * 1000,
    );
  } catch {
    return [];
  }
}

/** Keep accepted requests until their posts settle, including slow extractions. */
export function readPendingRequests(workspaceId: string | null | undefined): PendingRequest[] {
  return readStoredRequests(workspaceId).filter(r =>
    r.error || r.confirming || r.postIds?.length || Date.now() - r.clickedAt <= PENDING_MAX_AGE_MS,
  );
}

function write(
  workspaceId: string | null | undefined,
  requests: PendingRequest[],
): void {
  const key = storageKey(workspaceId);
  if (!key) return;
  try {
    if (requests.length === 0) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify({ requests }));
    window.dispatchEvent(new Event("wavegen:pending-generation-changed"));
  } catch {
    // sessionStorage unavailable — pending tiles are best-effort
  }
}

export function appendPendingRequest(
  workspaceId: string,
  request: PendingRequest,
): void {
  write(workspaceId, [...readStoredRequests(workspaceId), request]);
}

export function updatePendingRequest(
  workspaceId: string,
  clientRequestId: string,
  patch: Partial<PendingRequest>,
): void {
  write(
    workspaceId,
    readStoredRequests(workspaceId).map((r) =>
      r.clientRequestId === clientRequestId ? { ...r, ...patch } : r,
    ),
  );
}

export function removePendingRequest(
  workspaceId: string,
  clientRequestId: string,
): void {
  write(
    workspaceId,
    readStoredRequests(workspaceId).filter(
      (r) => r.clientRequestId !== clientRequestId,
    ),
  );
}

/** Mirrors the server's generation_requests doc id derivation. */
export async function generationRequestDocId(
  workspaceId: string,
  requestId: string,
): Promise<string | null> {
  if (!window.crypto?.subtle) return null;
  const data = new TextEncoder().encode(`${workspaceId}:${requestId}`);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Normalize both the HTTP response and persisted request failure. */
export function generationFailurePatch(data: {
  error?: unknown;
  errorType?: unknown;
  message?: unknown;
  suggestion?: unknown;
  fallback?: { type?: unknown } | null;
  url?: unknown;
}): Pick<PendingRequest, "error" | "errorCode" | "suggestion" | "fallback" | "confirming"> {
  const type = data.fallback?.type;
  return {
    confirming: false,
    ...(data.errorType === "no-source-images" ? { errorCode: "no-source-images" as const } : data.errorType === "pdf-unreadable" ? { errorCode: "pdf-unreadable" as const } : {}),
    error: typeof data.error === "string" && data.error
      ? data.error
      : typeof data.message === "string" && data.message
        ? data.message : "We couldn’t prepare posts from this input.",
    suggestion: typeof data.suggestion === "string" ? data.suggestion : undefined,
    fallback: (type === "paste_text" || type === "paste_transcript") &&
      typeof data.url === "string" && data.url
      ? { type, url: data.url } : undefined,
  };
}

/** Keep generation settings and other inputs when replacing an unreadable URL. */
export function composerForTextFallback(
  saved: GenerationRetry,
  fallback: PendingRequest["fallback"],
  unsupportedPdf = false,
): GenerationRetry {
  if (!fallback) return unsupportedPdf ? { ...saved, sources: saved.sources.filter(source => !source.documentId && !(source.type === "url" && /\.pdf(?:[?#]|$)/i.test(source.value))) } : saved;
  return { ...saved, sources: saved.sources.filter(
    source => !(source.type === "url" && source.value === fallback.url),
  ) };
}
